import { Link, useParams } from 'react-router-dom';
import { categories, hotArticles } from '@/mocks';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function CategoryPage() {
  const { slug } = useParams();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = slug
    ? hotArticles.filter((a) => a.categorySlug === slug)
    : [];

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: '/' },
      { '@type': 'ListItem', position: 2, name: category ? category.name : '分类', item: slug ? `/category/${slug}` : '/category' },
    ],
  };

  const webpageLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: category ? `${category.name} - 51吃瓜网站` : '全部分类 - 51吃瓜网站',
    description: category ? category.description : '浏览51吃瓜网站的全部内容分类',
    url: slug ? `/category/${slug}` : '/category',
    inLanguage: 'zh-CN',
    isPartOf: { '@type': 'WebSite', name: '51吃瓜网站', url: '/' },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-20 md:pt-24">
        <section className="relative py-12 md:py-16 flex items-center justify-center overflow-hidden bg-foreground-950">
          <div className="absolute inset-0">
            <img
              src={category ? category.image : 'https://readdy.ai/api/search-image?query=Abstract%20dark%20entertainment%20news%20background%2C%20moody%20red%20and%20black%20tones%2C%20cinematic%20spotlight%20effect%2C%20dramatic%20shadows%2C%20modern%20editorial%20style&width=1920&height=600&seq=cat-default-01&orientation=landscape'}
              alt={category ? category.name : '分类'}
              className="w-full h-full object-cover object-top opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60" />
          </div>
          <div className="relative z-10 container-custom text-center">
            <nav aria-label="面包屑导航" className="mb-3">
              <ol className="flex items-center justify-center gap-2 text-sm text-background-300">
                <li><Link to="/" className="hover:text-background-50 transition-colors">首页</Link></li>
                <li><i className="ri-arrow-right-s-line text-xs" /></li>
                <li className="text-background-100">{category ? category.name : '分类'}</li>
              </ol>
            </nav>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-background-50 mb-3">
              {category ? category.name : '全部分类'}
            </h1>
            <p className="text-sm md:text-base text-background-300 max-w-xl mx-auto">
              {category ? category.description : '浏览51吃瓜网站的全部内容分类'}
            </p>
          </div>
        </section>

        {slug && categoryArticles.length > 0 ? (
          <section className="py-10 md:py-14">
            <div className="container-custom">
              <div className="flex items-center justify-between mb-6 md:mb-8">
                <div className="flex items-center gap-2">
                  <span className="px-3 py-1 rounded-full border border-primary-400/30 text-primary-500 text-xs font-medium">
                    {categoryArticles.length} 篇文章
                  </span>
                  <h2 className="text-lg md:text-xl font-semibold text-foreground-950">最新爆料</h2>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group rounded-2xl overflow-hidden bg-background-100 hover:bg-background-200 border border-transparent hover:border-background-300 transition-all"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4 md:p-5">
                      <h3 className="text-sm md:text-base font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-500 line-clamp-2 mb-3">
                        {article.summary}
                      </p>
                      <div className="flex items-center justify-between text-xs text-foreground-400">
                        <span>{article.publishDate}</span>
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line" /> {article.views.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        ) : (
          <section className="py-10 md:py-14">
            <div className="container-custom">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className="group relative rounded-2xl overflow-hidden bg-background-100 border border-background-200 hover:border-primary-300 transition-all"
                  >
                    <div className="aspect-[3/2] overflow-hidden">
                      <img
                        src={cat.image}
                        alt={cat.name}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4 md:p-5">
                      <div className="flex items-center justify-between mb-2">
                        <h2 className="text-lg md:text-xl font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors">
                          {cat.name}
                        </h2>
                        <span className="text-xs text-foreground-400 bg-background-200 px-2 py-1 rounded-full">
                          {cat.articleCount}篇
                        </span>
                      </div>
                      <p className="text-sm text-foreground-500 line-clamp-2">
                        {cat.description}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(webpageLd) }} />
    </div>
  );
}